/* eslint-disable global-require */
/**
 * First we will load all of this project's JavaScript dependencies which
 * includes React and other helpers. It's a great starting point while
 * building robust, powerful web applications using React + Laravel.
 *
 *
 *
 * Next, we will create a fresh React component instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */

import './i18n';
import Config from '../Config/Config';
import { isDefault, isDefaultCasino, isGGWorld, isThankYouLotto, shouldUseTimeinnTemplate } from '../utils/template';

import loadComponent from './loadComponent';

const notificationsEnabled = process.env.MIX_NOTIFICATIONS_ENABLED?.toLowerCase() === 'true';
const winnersToastEnabled = process.env.MIX_WINNERS_TOAST_ENABLED?.toLowerCase() === 'true';
const otpPhoneVerificationEnabled = process.env.MIX_PHONE_VERIFICATION_ENABLED?.toLowerCase() === 'true';
const isCasinoEnabled = Config.isTrue('casino.is_enabled');

const componentsToLoadWithoutLoader: Record<string, string[]> = {
    'homepage/lottery-list/DrawDateCounterLoader': ['draw-date-counter'],
    'results/ResultsDetails': ['results-details'],
};
const componentsToLoad: Record<string, string[]> = {
    'auth/captcha/CaptchaWrapper': ['captcha-wrapper'],

    'latest-results/LatestResults': ['latest-results'],
    'latest-winners/LatestWinners': ['latest-winners'],

    'jackpot/JackpotBanner': ['jackpot-banner'],
    'current-jackpot/CurrentJackpot': ['lottery-counter'],

    'counters/UniversalDrawCounterLoader': ['universal-draw-counter'],

    'user/user-panel/UserPanel': ['user-panel'],
    'user/user-panel/BalanceElement': ['user-panel-balance'],

    'promo-codes/PromoCode': ['promo-code'],

    'notifications/NotificationsWrapper': ['notifications-wrapper'],
    'sales-growth/UserGiftWidget': ['sales-growth-gift-widget'],

    'sliders/Sliders': ['casino-sliders-component', 'sliders-component', 'sliders-component-bottom'],

    'faq/Faq': ['faq-component'],
    'faq/FaqCategory': ['faq-category-component'],

    'mobile-app-download-banner/MobileAppDownloadBanner': ['component--mobile-app-download-banner'],

    'homepage/News/News': ['homepage-news'],

    'Deposits/DepositFinalStatusRedirector': ['deposit-final-status-redirector'],

    'auth/AuthPage': ['auth_page_index'],

    'homepage/sliders/CasinoGamesCarouselSliders': ['casino-games-carousel-sliders'],
    'lootbox/Lootbox': ['component--lootbox'],
    'lootbox/RedeemByCode': ['component--lootbox-redeem'],

    'my-account/History/MyUserAccountChanges': ['account-my-user-account-changes'],
    'default/MyAccount/MarketingPreferences/MarketingPreferences': ['my-account-marketing-preferences'],
};

if (notificationsEnabled) {
    componentsToLoadWithoutLoader['default/HeaderNotificationBell'] = ['component--header-notification-enhancer'];
}

if (winnersToastEnabled) {
    componentsToLoad['winners-toast/WinnersToast'] = ['winners-toast'];
}

if (otpPhoneVerificationEnabled) {
    componentsToLoad['auth/register/OneStep'] = ['otp-step-container'];
}

if (isDefault()) {
    Object.assign(componentsToLoad, {
        'default/GuestHeaderMenu': ['component--guest-header-menu'],
        'default/Homepage/Casino/CasinoWrapper': ['casino-wrapper'],

        'default/MyAccount/AccountMenu': ['account-menu'],
        'default/MyAccount/AccountMenuHeader': ['account-menu-header'],
        'default/MyAccount/Promote/Promote': ['my-account-promote'],
        'default/MyAccount/Overview/TicketsOverview': ['my-account-tickets-overview'],
        'default/MyAccount/Overview/TransactionsOverview': ['my-account-transactions-overview'],
        'default/MyAccount/Overview/CasinoTransactionsOverview': ['my-account-casino-transactions-overview'],
        'default/MyAccount/BankAccounts/BankAccounts': ['bank-accounts'],
        'default/MyAccount/Transfers/Transfers': ['transfers'],

        'default/MySalesAccount/Vouchers/PurchasedVouchers': ['my-sales-account-purchased-vouchers'],
        'default/MySalesAccount/Vouchers/UsedVouchers': ['my-sales-account-used-vouchers'],

        'default/Checkout/BasketWrapper': ['checkout'],

        'default/Bundles/Bundles': ['bundles'],

        'default/Rules/SingleRules/PickRulesControls': ['single-rules-pick-controls'],
        'default/Rules/SingleRules/KenoRulesControls': ['single-rules-keno-controls'],

        'default/Widgets/PlayLottery': ['widget-play-lottery'],
        'default/Widgets/PlayLotterySlider': ['widget-play-lottery-slider'],
    });

    Object.assign(componentsToLoadWithoutLoader, {
        'default/Header': ['component--header'],
        'default/HeaderBalance': ['component--header-balance'],
        'default/Homepage/Slider/LotterySlider': ['lottery-slider'],
        'default/Results/Results': ['results'],
        'default/Rules/Rules': ['rules-controls'],
    });
}

if (isGGWorld()) {
    componentsToLoad['homepage/header/templates/gg-world/MainHeader'] = ['main-header'];
    componentsToLoad['homepage/rules/templates/gg-world/RuleTiers'] = ['rule-tiers'];
}

if (isThankYouLotto()) {
    componentsToLoad['my-account/MyTickets/SingleLootboxTicket'] = ['component--account-my-tickets-lootbox-single'];
}

if (shouldUseTimeinnTemplate()) {
    componentsToLoad['pwa-banner/PwaBanner'] = ['component--pwa-banner'];
}

if (isDefaultCasino()) {
    // The casino header pre-renders the guest login/register buttons server-side, so load
    // the user panel without a spinner: the existing markup is preserved during chunk load
    // and the seeded guest state renders the buttons instantly (no loader, no API wait).
    delete componentsToLoad['user/user-panel/UserPanel'];
    componentsToLoadWithoutLoader['user/user-panel/UserPanel'] = ['user-panel'];
}

Object.entries(componentsToLoadWithoutLoader).forEach(([component, selectors]) => {
    loadComponent(component, selectors, null);
});

Object.entries(componentsToLoad).forEach(([component, selectors]) => {
    loadComponent(component, selectors);
});

// TODO(vite-F3): conditional require() — intentionally NOT converted in F0. A static import would
// hoist ReactCasino's module body above the loadComponent() loops above and evaluate it on
// casino-disabled templates too; a dynamic import() would make the mounting asynchronous. Both are
// behaviour changes, so this is left for F3 together with ReactCasino.ts (see f0-js-codemod-report.md).
if (isCasinoEnabled) {
    require('../casino/Base/ReactCasino');
}

document.addEventListener('DOMContentLoaded', function () {
    if ('Notification' in window && Notification.permission !== 'granted') {
        Notification.requestPermission();
    }
});
